import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CalendarClock, CalendarOff } from 'lucide-react';
import { Card } from '../../../components/Card';
import { Badge } from '../../../components/Badge';
import { cn } from '../../../lib/utils';
import type {
  ApprovalQueue as Queue, DayStatus, PendingLeave, PendingRegularization,
} from '../../../store/peopleDashboardStore';

/* ───────────────────────────── formatting ───────────────────────────── */

/** 'YYYY-MM-DD' → local Date (never via UTC, which can shift the day). */
export function parseDay(d: string): Date {
  const [y, m, day] = d.slice(0, 10).split('-').map(Number);
  return new Date(y, (m || 1) - 1, day || 1);
}

export const fmtDay = (d?: string | null) =>
  d ? parseDay(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '—';

export const fmtWeekday = (d: string) =>
  parseDay(d).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });

export const fmtTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '—';

/** Clock time from a Postgres TIME value such as '09:30:00+05:30'. */
export const fmtClock = (t?: string | null) => (t ? t.slice(0, 5) : '');

export const fmtRange = (a: string, b: string) => (a === b ? fmtDay(a) : `${fmtDay(a)} – ${fmtDay(b)}`);

export function fmtHours(h: number) {
  const mins = Math.max(0, Math.round(h * 60));
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`;
}

export function fmtMoney(amount: number, currency = 'INR') {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${currency} ${amount.toLocaleString()}`;
  }
}

/** "today", "tomorrow", "in 5 days" relative to the server's business date. */
export function daysFrom(today: string, d: string) {
  const diff = Math.round((parseDay(d).getTime() - parseDay(today).getTime()) / 86_400_000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff < 0) return `${-diff}d ago`;
  return `In ${diff} days`;
}

/* ───────────────────────────── building blocks ───────────────────────── */

export function SectionHeading({ title, description, children }: {
  title: string; description?: string; children?: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-2">
      <div>
        <h2 className="text-foreground font-display text-section-title">{title}</h2>
        {description && <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>}
      </div>
      {children}
    </div>
  );
}

/** Card with a title row and an optional "View all" link. */
export function Panel({ title, description, link, className, bodyClassName, children }: {
  title: string;
  description?: string;
  link?: { label: string; to: string };
  className?: string;
  bodyClassName?: string;
  children: React.ReactNode;
}) {
  return (
    <Card className={cn('flex flex-col', className)}>
      <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-3">
        <div className="min-w-0">
          <h3 className="text-foreground font-display text-card-title">{title}</h3>
          {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
        </div>
        {link && (
          <Link to={link.to} className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-primary hover:underline">
            {link.label} <ArrowRight className="size-3" />
          </Link>
        )}
      </div>
      <div className={cn('flex-1 px-4 pb-4', bodyClassName)}>{children}</div>
    </Card>
  );
}

type Tone = 'default' | 'primary' | 'success' | 'warning' | 'destructive' | 'info';

const toneText: Record<Tone, string> = {
  default: 'text-foreground',
  primary: 'text-primary',
  success: 'text-success-strong',
  warning: 'text-warning-strong',
  destructive: 'text-destructive-strong',
  info: 'text-info-strong',
};
const toneIcon: Record<Tone, string> = {
  default: 'bg-secondary text-muted-foreground',
  primary: 'bg-primary/10 text-primary',
  success: 'bg-success/10 text-success-strong',
  warning: 'bg-warning/10 text-warning-strong',
  destructive: 'bg-destructive/10 text-destructive-strong',
  info: 'bg-info/10 text-info-strong',
};

/** Compact KPI tile. Clickable when `to` is given. */
export function Kpi({ label, value, hint, icon, tone = 'default', to }: {
  label: string; value: React.ReactNode; hint?: React.ReactNode; icon?: React.ReactNode; tone?: Tone; to?: string;
}) {
  const body = (
    <Card interactive={!!to} className="h-full p-4">
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        {icon && (
          <span className={cn('flex size-7 items-center justify-center rounded-md [&_svg]:size-4', toneIcon[tone])}>
            {icon}
          </span>
        )}
      </div>
      <div className={cn('mt-2 font-display text-metric tabular-nums leading-none', toneText[tone])}>{value}</div>
      {hint && <p className="mt-2 truncate text-xs text-muted-foreground">{hint}</p>}
    </Card>
  );
  return to ? <Link to={to} className="block h-full rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50">{body}</Link> : body;
}

export function Empty({ icon, text, className }: { icon?: React.ReactNode; text: string; className?: string }) {
  return (
    <div className={cn('flex h-full min-h-24 flex-col items-center justify-center gap-2 py-6 text-center text-sm text-muted-foreground', className)}>
      {icon && <span className="text-muted-foreground/60 [&_svg]:size-6">{icon}</span>}
      {text}
    </div>
  );
}

const DAY_STATUS: Record<DayStatus, { label: string; variant: 'success' | 'info' | 'warning' | 'muted' }> = {
  checked_in: { label: 'Working', variant: 'success' },
  checked_out: { label: 'Checked out', variant: 'info' },
  on_leave: { label: 'On leave', variant: 'warning' },
  not_checked_in: { label: 'Not checked in', variant: 'muted' },
};

export function DayStatusBadge({ status }: { status: DayStatus }) {
  const s = DAY_STATUS[status] ?? DAY_STATUS.not_checked_in;
  return <Badge variant={s.variant} dot>{s.label}</Badge>;
}

export function RequestStatusBadge({ status }: { status: string }) {
  const s = (status || '').toLowerCase();
  const variant = s === 'approved' || s === 'paid' || s === 'processed' ? 'success'
    : s === 'rejected' || s === 'cancelled' ? 'destructive'
    : s === 'pending' || s === 'draft' ? 'warning' : 'muted';
  return <Badge variant={variant} className="capitalize">{s || 'unknown'}</Badge>;
}

/** Horizontal bar list — lighter than a chart for a handful of categories. */
export function BarList({ rows, total }: { rows: { label: string; value: number }[]; total?: number }) {
  const max = Math.max(1, total ?? Math.max(...rows.map((r) => r.value), 0));
  return (
    <ul className="space-y-2.5">
      {rows.map((r) => (
        <li key={r.label}>
          <div className="mb-1 flex items-center justify-between gap-2 text-sm">
            <span className="truncate text-foreground">{r.label}</span>
            <span className="tabular-nums text-muted-foreground">{r.value}</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
            <div className="h-full rounded-full bg-primary" style={{ width: `${Math.round((r.value / max) * 100)}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

/* ───────────────────────────── approvals ─────────────────────────────── */

/** Pending leave + regularization requests, with a switch between the two. */
export function ApprovalQueuePanel({ leave, regularizations, today, className, scopeLabel }: {
  leave: Queue<PendingLeave>;
  regularizations: Queue<PendingRegularization>;
  today: string;
  className?: string;
  scopeLabel: string;
}) {
  const [tab, setTab] = useState<'leave' | 'reg'>(leave.total || !regularizations.total ? 'leave' : 'reg');
  const tabs = [
    { id: 'leave' as const, label: 'Leave', count: leave.total },
    { id: 'reg' as const, label: 'Regularization', count: regularizations.total },
  ];

  return (
    <Panel
      title="Pending approvals"
      description={scopeLabel}
      link={{ label: 'Review all', to: tab === 'leave' ? '/people/leave' : '/people/attendance' }}
      className={className}
    >
      <div className="mb-3 inline-flex rounded-lg bg-secondary p-0.5">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              'inline-flex h-7 items-center gap-1.5 rounded-md px-3 text-xs font-medium transition-colors',
              tab === t.id ? 'bg-card text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {t.label}
            <span className={cn('rounded-full px-1.5 tabular-nums', t.count ? 'bg-warning/15 text-warning-strong' : 'bg-card/60')}>
              {t.count}
            </span>
          </button>
        ))}
      </div>

      {tab === 'leave' ? (
        leave.items.length === 0 ? (
          <Empty icon={<CalendarOff />} text="No leave requests waiting for approval" />
        ) : (
          <ul className="divide-y divide-border">
            {leave.items.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">{r.employee}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {r.type} · {fmtRange(r.startDate, r.endDate)} · {r.days} {r.days === 1 ? 'day' : 'days'}
                  </p>
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">{daysFrom(today, r.startDate)}</span>
              </li>
            ))}
          </ul>
        )
      ) : regularizations.items.length === 0 ? (
        <Empty icon={<CalendarClock />} text="No attendance corrections waiting for approval" />
      ) : (
        <ul className="divide-y divide-border">
          {regularizations.items.map((r) => (
            <li key={r.id} className="flex items-center justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">{r.employee}</p>
                <p className="truncate text-xs text-muted-foreground">{fmtDay(r.date)} · {r.reason || 'No reason given'}</p>
              </div>
              <span className="shrink-0 text-xs text-muted-foreground">{daysFrom(today, r.date)}</span>
            </li>
          ))}
        </ul>
      )}

      {(tab === 'leave' ? leave.total > leave.items.length : regularizations.total > regularizations.items.length) && (
        <p className="pt-2 text-xs text-muted-foreground">
          Showing {tab === 'leave' ? leave.items.length : regularizations.items.length} of {tab === 'leave' ? leave.total : regularizations.total}
        </p>
      )}
    </Panel>
  );
}
